import { useState } from "react";
import { format } from "date-fns";
import { Copy, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";

export const CATEGORIES = ["SUV", "SEDAN", "HATCHBACK", "TRUCK", "SPORTS"];

export const CATEGORY_LABEL = {
  SUV: "SUV",
  SEDAN: "Sedan",
  HATCHBACK: "Hatchback",
  TRUCK: "Truck",
  SPORTS: "Sports",
};

// Gas below this (ETH) can't cover a withdrawal; mirrors GAS_TOPUP_THRESHOLD_ETH in chain-service.
export const LOW_GAS_ETH = 0.005;
export const ADMIN_LOW_GAS_ETH = 1;

export const fmtUsd = (cents) =>
  `$${((cents || 0) / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const fmtNum = (n) => (n == null ? "—" : Number(n).toLocaleString("en-US"));

export const fmtEth = (eth, digits = 4) =>
  eth == null ? "—" : `${Number(eth).toLocaleString("en-US", { maximumFractionDigits: digits })} ETH`;

export const fmtDateTime = (iso) => (iso ? format(new Date(iso), "dd/MM/yyyy HH:mm") : "—");

export const shortHex = (hex, head = 6, tail = 4) =>
  !hex ? "—" : hex.length <= head + tail + 2 ? hex : `${hex.slice(0, head)}…${hex.slice(-tail)}`;

// Tones are derived from semantic tokens so badges stay on-system.
const TONES = {
  success: "var(--zc-success)",
  warn: "var(--zc-warn)",
  danger: "var(--zc-danger)",
  info: "var(--zc-secondary)",
  neutral: "var(--zc-text-3)",
};

export const toneStyle = (tone) => ({
  background: `color-mix(in srgb, ${TONES[tone]} 13%, transparent)`,
  color: `color-mix(in srgb, ${TONES[tone]} 72%, var(--zc-text))`,
});

export function StatusBadge({ tone = "neutral", children, icon: Icon, testid }) {
  return (
    <span className="zc-badge inline-flex items-center gap-1 whitespace-nowrap" style={toneStyle(tone)} data-testid={testid}>
      {Icon && <Icon size={11} />}
      {children}
    </span>
  );
}

export const ORDER_STATUS = {
  AWAITING_PAYMENT: { label: "Awaiting payment", tone: "warn" },
  PAYMENT_FAILED: { label: "Payment failed", tone: "danger" },
  PAID: { label: "Paid", tone: "info" },
  TRANSFERRING: { label: "Transferring", tone: "info" },
  COMPLETED: { label: "Completed", tone: "success" },
  TRANSFER_FAILED: { label: "Transfer failed", tone: "danger" },
};

export const WITHDRAWAL_STATUS = {
  PENDING: { label: "Pending", tone: "warn" },
  COMPLETED: { label: "Completed", tone: "success" },
  FAILED: { label: "Failed", tone: "danger" },
};

export function collectionStatus(c) {
  if (c.status === "MINTED" && c.onChain === false) return { label: "Not on chain", tone: "danger" };
  if (c.status === "MINTED") return { label: "Live", tone: "success" };
  if (c.status === "PENDING_MINT") return { label: "Minting", tone: "warn" };
  return { label: "Mint failed", tone: "danger" };
}

export function CopyHex({ value, head, tail, label, className = "" }) {
  const [copied, setCopied] = useState(false);
  if (!value) return <span className="text-[var(--zc-text-3)]">—</span>;
  const copy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(`${label || "Value"} copied`);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Could not copy");
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      title={value}
      aria-label={`Copy ${label || "value"} ${value}`}
      className={"inline-flex items-center gap-1.5 whitespace-nowrap font-mono text-xs text-[var(--zc-text-2)] hover:text-[var(--zc-secondary)] transition-colors " + className}
    >
      <span className="whitespace-nowrap">{shortHex(value, head, tail)}</span>
      {copied ? <Check size={12} className="text-[var(--zc-success)]" /> : <Copy size={12} className="opacity-60" />}
    </button>
  );
}

export function Investor({ investor, fallbackAddress }) {
  if (!investor?.name && !investor?.email) {
    return <CopyHex value={fallbackAddress || investor?.walletAddress} label="Wallet address" />;
  }
  return (
    <div className="min-w-0">
      <div className="font-semibold truncate">{investor.name || investor.email}</div>
      {investor.name && investor.email && (
        <div className="text-2xs text-[var(--zc-text-3)] truncate mt-0.5">{investor.email}</div>
      )}
    </div>
  );
}

export function FilterChips({ options, value, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-1" role="group" data-testid={testid}>
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => onChange(o.id)}
          aria-pressed={value === o.id}
          className={
            "px-3 h-8 text-xs font-semibold rounded-md border transition-colors "
            + (value === o.id
              ? "border-[var(--zc-secondary)] text-[var(--zc-secondary)] bg-[var(--zc-secondary-soft)]"
              : "border-[var(--zc-border)] text-[var(--zc-text-3)] hover:text-[var(--zc-text)]")
          }
        >
          {o.label}
          {o.count != null && <span className="ml-1.5 opacity-70 tabular-nums">{o.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function SearchInput({ value, onChange, placeholder, label }) {
  return (
    <input
      type="search"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      aria-label={label || placeholder}
      className="zc-input h-9 px-3 w-full sm:w-72"
    />
  );
}

export function EmptyState({ icon: Icon, title, hint, action }) {
  return (
    <div className="px-6 py-14 text-center">
      {Icon && (
        <div className="w-11 h-11 rounded-xl mx-auto mb-3 grid place-items-center bg-[var(--zc-bg-3)]">
          <Icon size={18} className="text-[var(--zc-text-3)]" />
        </div>
      )}
      <div className="font-semibold text-sm">{title}</div>
      {hint && <div className="text-xs text-[var(--zc-text-3)] mt-1 max-w-sm mx-auto">{hint}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function RowsSkeleton({ rows = 4 }) {
  return (
    <div aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="px-4 sm:px-6 py-4 border-b border-[var(--zc-border)] last:border-0 flex items-center gap-4 animate-pulse">
          <div className="w-10 h-10 rounded-lg bg-[var(--zc-bg-3)]" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-1/3 rounded bg-[var(--zc-bg-3)]" />
            <div className="h-2.5 w-1/5 rounded bg-[var(--zc-bg-3)]" />
          </div>
          <div className="h-6 w-20 rounded-full bg-[var(--zc-bg-3)]" />
        </div>
      ))}
    </div>
  );
}

export function ErrorBanner({ message, onRetry }) {
  return (
    <div className="m-4 p-3 rounded-lg text-sm flex flex-wrap items-center justify-between gap-2" style={toneStyle("danger")} role="alert">
      <span>{message}</span>
      {onRetry && (
        <button type="button" onClick={onRetry} className="px-3 h-8 zc-btn-ghost text-xs font-semibold">Retry</button>
      )}
    </div>
  );
}

export function SectionHeader({ title, count, children }) {
  return (
    <div className="px-4 sm:px-6 py-3 border-b border-[var(--zc-border)] flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="text-xs uppercase tracking-widest text-[var(--zc-text-3)] font-semibold">{title}</div>
          {count != null && (
            <span className="text-2xs font-bold px-2 py-0.5 rounded-full bg-[var(--zc-secondary-soft)] text-[var(--zc-secondary)] tabular-nums">{count}</span>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}

/** Confirmation for on-chain writes — they cost gas and cannot be reverted. */
export function ConfirmChainDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  tone = "primary",
  busy,
  onConfirm,
  children,
}) {
  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {children}
        <DialogFooter className="gap-2 sm:gap-2">
          <button type="button" className="zc-btn-ghost px-4 h-10 text-sm" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={(tone === "danger" ? "zc-btn-outline-primary" : "zc-btn-secondary") + " px-5 h-10 text-sm font-semibold inline-flex items-center justify-center gap-2"}
            data-testid="bc-confirm"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            {busy ? "Confirming on-chain…" : confirmLabel}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
